import {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {DEPLOY_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {setDeployPipelines} from '@/store/onboardingSlices'
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'

const initialDeploy = {
  deployJob: '',
  deployProdBranch: '',
  deployHotfixBranch: '',
  deployProdEnvSuffix: '',
}

import type {Deploy} from '@/store/onboardingSlices'
import {Button, Feedback, Label} from 'pepsico-ds'
import type {RootState} from '@/store'
import CustomInput from '@/components/common/CustomInputWrapper'

const DeployWidget = ({onValidationChange}: {onValidationChange?: (hasError: boolean) => void}) => {
  const dispatch = useDispatch()
  type Suggestion = {id: string; label: string; fullObject: any} // Simplified, no fullObject

  const deploys = useSelector((state: RootState) => state.deploy)

  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  // Track touched fields for each deploy
  const [touched, setTouched] = useState<{
    [idx: number]: {
      deployJob?: boolean
      deployProdBranch?: boolean
      deployHotfixBranch?: boolean
      deployProdEnvSuffix?: boolean
    }
  }>({})

  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  // Sync deploys state to Redux
  useEffect(() => {
    dispatch(setDeployPipelines(deploys))
  }, [deploys, dispatch])

  const updateDeploy = (idx: number, field: string, value: string | {id: string; label: string; value: string}) => {
    dispatch(setDeployPipelines(deploys.map((d, i) => (i === idx ? {...d, [field]: value} : d))))
  }
  const handleBlur = (idx: number, field: keyof Deploy) => {
    setTouched(prev => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {}),
        [field]: true,
      },
    }))
  }

  const addDeploy = () => {
    dispatch(setDeployPipelines([...deploys, {...initialDeploy}]))
  }

  const removeDeploy = (idx: number) => {
    dispatch(setDeployPipelines(deploys.filter((_, i) => i !== idx)))
    setTouched(prev => {
      const copy = {...prev}
      delete copy[idx]
      return copy
    })
  }
  // Validation: show errors for all required fields if any field touched
  useEffect(() => {
    const anyTouched = anyWidgetFieldTouched(touched)
    const hasError =
      anyTouched && hasEmptyRequiredFields(['deployJob', 'deployProdBranch', 'deployProdEnvSuffix'], deploys)
    if (onValidationChange) onValidationChange(hasError)
  }, [deploys, touched, onValidationChange])

  const widgetTouched = anyWidgetFieldTouched(touched)

  // Utility to check if deployJob is empty (handles string or object)
  const isDeployJobEmpty = (deployJob: any) => {
    if (!deployJob) return true
    if (typeof deployJob === 'string') return deployJob.trim() === ''
    if (typeof deployJob === 'object') {
      return !deployJob.label && !deployJob.description
    }
    return false
  }

  return (
    <>
      {deploys.map((deploy, idx) => (
        <CardWrapper
          cardHeader={{
            title: `${DEPLOY_WIDGET.label} ${idx + 1}`,
            rightAccessory:
              idx > 0
                ? {
                    iconButtons: [
                      {
                        icon: 'delete',
                        onClick: () => removeDeploy(idx),
                      },
                    ],
                  }
                : {
                    iconButtons: [
                      {
                        icon: 'delete_outline',
                        onClick: () => {
                          dispatch(
                            setDeployPipelines([
                              {deployJob: '', deployProdBranch: '', deployHotfixBranch: '', deployProdEnvSuffix: ''},
                            ]),
                          )
                          setTouched({})
                        },
                      },
                    ],
                  },
          }}
          cardBody={{
            type: 'slot',
            slot: <></>,
          }}
          state="non-interactive"
          type="basic"
          key={idx}
        >
          <form className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-start gap-4">
              <div className="flex w-[282px] flex-col items-start">
                <Label className={sharedStyles.autocompleteLabelFont} label={DEPLOY_WIDGET.label} />
                <AutoCompleteWrapper
                  icon="keyboard_arrow_down"
                  limitSuggestions={30}
                  placeholderText={DEPLOY_WIDGET.placeholder}
                  onClear={() => updateDeploy(idx, 'deployJob', {id: '', label: '', value: ''})}
                  suggestions={suggestions}
                  isAutoComplete={true}
                  isLeadingIcon={false}
                  searchTerm={
                    typeof deploy.deployJob === 'string'
                      ? deploy.deployJob
                      : deploy.deployJob && typeof deploy.deployJob === 'object'
                        ? 'description' in deploy.deployJob && typeof deploy.deployJob.description === 'string'
                          ? deploy.deployJob.description
                          : 'label' in deploy.deployJob && typeof deploy.deployJob.label === 'string'
                            ? deploy.deployJob.label
                            : undefined
                        : undefined
                  }
                  onUpdate={async (query: string) => {
                    try {
                      const {searchItemsBySearchField} = await import('@/services/team/teamService')
                      const results = await searchItemsBySearchField('Deployment', query, 'description')
                      const mapped = Array.isArray(results)
                        ? results
                            .map((item: any) => ({
                              id: item.id || item._id || item.value || item.label,
                              label:
                                item.description && item.applicationId
                                  ? `${item.description} (${item.applicationId})`
                                  : item.description || 'Unknown Pipeline',
                              fullObject: item,
                            }))
                            .filter(option => option.label && typeof option.label === 'string')
                        : []
                      setSuggestions(mapped)
                    } catch (err) {
                      console.log('Failed to load suggestions')
                    }
                  }}
                  onSelectOption={(option: any) => updateDeploy(idx, 'deployJob', option)}
                  onBlur={() => handleBlur(idx, 'deployJob')}
                />

                <Feedback
                  variant="info"
                  className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                  message="Name of the deploy pipeline to be monitored."
                />
                {widgetTouched && isDeployJobEmpty(deploy.deployJob) && (
                  <div className="mt-1 text-xs text-red-500">Required</div>
                )}
              </div>
              <div className="flex w-[282px] flex-col items-start">
                <CustomInput
                  placeholder="Enter branch/tags names"
                  label="Production Deployment (Branch/Tag)"
                  name={`deployProdBranch_${idx}`}
                  value={deploy.deployProdBranch}
                  onChange={e => updateDeploy(idx, 'deployProdBranch', e.target.value)}
                  onBlur={() => handleBlur(idx, 'deployProdBranch')}
                  error={widgetTouched && !deploy.deployProdBranch ? 'Required' : ''}
                  helperText={DEPLOY_WIDGET.prodBranchHelper}
                />
              </div>
            </div>
            <div className="flex w-full items-start gap-4">
              <div className="flex w-[282px] flex-col items-start">
                <CustomInput
                  placeholder="Enter branch/tags names"
                  label="Hotfix Deployment (Branch/Tag)"
                  name={`deployHotfixBranch_${idx}`}
                  value={deploy.deployHotfixBranch}
                  onChange={e => updateDeploy(idx, 'deployHotfixBranch', e.target.value)}
                  onBlur={() => handleBlur(idx, 'deployHotfixBranch')}
                  helperText={DEPLOY_WIDGET.hotfixBranchHelper}
                />
              </div>
              <div className="flex w-[282px] flex-col items-start">
                <CustomInput
                  placeholder={DEPLOY_WIDGET.prodEnvPlaceholder}
                  label={DEPLOY_WIDGET.prodEnvLabel}
                  name={`deployProdEnvSuffix_${idx}`}
                  value={deploy.deployProdEnvSuffix}
                  onChange={e => updateDeploy(idx, 'deployProdEnvSuffix', e.target.value)}
                  onBlur={() => handleBlur(idx, 'deployProdEnvSuffix')}
                  error={widgetTouched && !deploy.deployProdEnvSuffix ? DEPLOY_WIDGET.required : ''}
                  helperText={DEPLOY_WIDGET.prodEnvHelper}
                />
              </div>
            </div>
          </form>
        </CardWrapper>
      ))}
      <div className={sharedStyles.addPipelineButton}>
        <Button
          type="button"
          iconTrailing="add"
          size="medium"
          style={{marginTop: 8}}
          text={DEPLOY_WIDGET.addPipeline}
          variant="tertiary"
          onClick={() => addDeploy()}
        />
      </div>
      <Label
        className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
        label={DEPLOY_WIDGET.pipelineHelper}
      />
    </>
  )
}

export default DeployWidget
